<template>
  <BaseWidget 
    title="Tasks handled automatically"
    :show-info-icon="true"
    info-tooltip="Total number of actions performed by your automations in the current billing period." 
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/9500741-what-is-an-automation', text: 'Learn about Automations' }"
    :automations-count="false"
    :widget-type="'actions-this-period'"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :compact="focused"
        :show-table="!focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
      :class="{ 'focused': focused }"
    >
      <MetricValue
        :value="actionsCount"
        :change="changePercentage"
        subtitle="Current period"
        :has-data-table="topAutomations.length > 0"
      >
        <template
          v-if="hasMore"
          #subtitle-suffix
        >
          <span
            class="focused-view-all-link"
            @click="openViewAllPanel"
          >View all</span>
        </template>
      </MetricValue>
      <div
        v-if="!focused && topAutomations.length > 0"
        class="actions-table-outer"
      >
        <div class="actions-table">
          <table>
            <thead>
              <tr>
                <th>Automation</th>
                <th class="text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in topAutomations"
                :key="item.workflowId || index"
                :class="{ 'action-row': true, 'clickable': item.workflowId }"
                @click="item.workflowId ? openWorkflowGuarded(item.workflowId) : null"
              >
                <td>{{ item.name }}</td>
                <td class="text-right">
                  {{ item.chargeable }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, onUnmounted } from 'vue'
import BaseWidget from './BaseWidget.vue'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import MetricValue from './MetricValue.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useActionsThisPeriodData } from '../composables/useWidgetData.js'
import { isRateLimitError } from '../utils/errorUtils.js'
import { pushAdminSidePanel } from '../composables/useOptixPanel.js'

const props = defineProps({
  priority: {
    type: Number,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const { data, loading, error, cleanup } = useActionsThisPeriodData({
  priority: props.priority ?? 1 // Use provided priority or default to 1
})

const actionsCount = computed(() => {
  const count = data.value?.workflowActionsCount?.chargeable
  if (count === null || count === undefined) return '0'
  return count.toString()
})

const actionsCountNum = computed(() => {
  const count = data.value?.workflowActionsCount?.chargeable
  if (count === null || count === undefined) return 0
  return Number(count) || 0
})

const changePercentage = computed(() => {
  // No change percentage available for this metric
  return null
})

// All automations sorted by chargeable actions
const allAutomations = computed(() => {
  const items = data.value?.workflowActionsCount?.data ?? []
  return [...items]
    .sort((a, b) => (b.chargeable ?? 0) - (a.chargeable ?? 0))
    .map((item) => ({
      workflowId: item.workflow?.workflow_id,
      name: item.workflow?.name ?? 'Unknown',
      chargeable: item.chargeable ?? 0
    }))
})

// Top 3 automations for the widget table
const topAutomations = computed(() => allAutomations.value.slice(0, 3))

// Whether there are more automations beyond the top 3
const hasMore = computed(() => allAutomations.value.length > 3)

// Open "View all" in native Optix side panel
const openViewAllPanel = () => {
  pushAdminSidePanel('view-all-actions')
}

// Extract parameters from URL for parent window communication
const getUrlParams = () => {
  let clientId = ''
  let canvasUUID = ''
  const urlParams = new URLSearchParams(window.location.search)
  clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
  canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  if (!clientId || !canvasUUID) {
    try {
      if (document.referrer) {
        const referrerUrl = new URL(document.referrer)
        const referrerParams = new URLSearchParams(referrerUrl.search)
        if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
        if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
      }
    } catch (e) {
      // Ignore
    }
  }
  if (!clientId || !canvasUUID) {
    try {
      if (window.parent && window.parent !== window) {
        const parentUrl = new URL(window.parent.location.href)
        const parentParams = new URLSearchParams(parentUrl.search)
        if (!clientId) clientId = parentParams.get('client_id') || parentParams.get('clientId') || ''
        if (!canvasUUID) canvasUUID = parentParams.get('canvasUUID') || parentParams.get('canvas_uuid') || parentParams.get('canvasUuid') || ''
      }
    } catch (e) {
      // CORS error - parent window is on different domain, ignore
    }
  }
  return { clientId, canvasUUID }
}

// Open workflow view when row is clicked
const { guard } = useTrialModeGuard()
const openWorkflowGuarded = guard((workflowId) => {
  if (!workflowId) return
  const params = getUrlParams()
  const generateCanvasUUID = () => {
    const timestamp = Date.now()
    const random = Math.floor(Math.random() * 1000000).toString().padStart(6, '0')
    return `${timestamp}-${random}`
  }
  const finalCanvasUUID = params.canvasUUID || generateCanvasUUID()
  const directMessage = {
    workflowId: workflowId.toString(),
    canvasUUID: finalCanvasUUID,
    client_id: params.clientId || undefined
  }
  const commandMessage = { command: 'WorkflowView', payload: directMessage }
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(directMessage, '*')
      setTimeout(() => window.parent.postMessage(commandMessage, '*'), 50)
      if (window.top && window.top !== window && window.top !== window.parent) {
        window.top.postMessage(directMessage, '*')
        setTimeout(() => window.top.postMessage(commandMessage, '*'), 50)
      }
    }
  } catch (error) {
    console.error('Error posting WorkflowView message:', error)
  }
})

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: 12px;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.actions-table-outer {
  flex: 1;
  min-height: 0;
  margin-top: 0;
  padding: 0;
  overflow-x: auto;
  overflow-y: hidden;
}

.actions-table {
  margin-top: 0;
  padding: 0;
}

.actions-table table {
  width: 100%;
  min-width: 280px;
  border-collapse: collapse;
  table-layout: fixed;
}

.actions-table thead {
  background: white;
}

.actions-table th {
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  padding: 8px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.actions-table th.text-right {
  text-align: right;
  width: 80px;
  white-space: nowrap;
}

.actions-table tbody tr {
  height: 56px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.actions-table tbody tr.clickable {
  cursor: pointer;
}

.actions-table tbody tr.clickable:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.actions-table tbody tr:last-child {
  border-bottom: none;
}

.actions-table td {
  padding: 12px 8px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
}

.actions-table td:first-child {
  max-width: 230px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.actions-table td.text-right {
  text-align: right;
  font-weight: 600;
  white-space: nowrap;
}

.view-all-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 32px;
  padding-top: 4px;
  flex-shrink: 0;
}

.view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 400;
  cursor: pointer;
  transition: color 0.2s ease;
}

.view-all-link:hover {
  color: #2563eb;
}

.focused-view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
  flex-shrink: 0;
}

.focused-view-all-link:hover {
  color: #2563eb;
}
</style>
